import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Import i18n configuration
import './utils/i18n';

// Context
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AuthModalProvider } from './contexts/AuthModalContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider, useToast } from './contexts/ToastContext';

// Pages
import About from './pages/About';
import SearchPage from './pages/Search';
import Dashboard from './pages/Dashboard';
import Tickets from './pages/Tickets';
import Favorites from './pages/Favorites';
import EventDetails from './pages/EventDetails';
import Checkout from './pages/Checkout';
import PaymentSuccess from './pages/PaymentSuccess';
import AdminDashboard from './pages/AdminDashboard';
import AdminLoginForm from './components/auth/AdminLoginForm';
import MasterDashboard from './pages/MasterDashboard';
import NotFound from './pages/NotFound';

// Components
import Header from './components/Header';
import BottomNavBar from './components/BottomNavBar';
import WhatsAppChat from './components/WhatsAppChat';
import ErrorBoundary from './components/ErrorBoundary';
import ToastContainer from './components/ui/ToastContainer';
import AuthModal from './components/auth/AuthModal';
import SitePasswordGate from './components/SitePasswordGate';

// CSS
import './App.css';

const AppContentInner: React.FC = () => {
  const { i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { toasts, removeToast } = useToast();

  // Apply RTL/LTR direction to document
  useEffect(() => {
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return (
    <Router>
      <div className={`App min-h-screen theme-transition ${i18n.language === 'ar' ? 'rtl' : 'ltr'}`} style={{background: 'var(--liquid-gradient-bg)'}}>
        {/* Header - Available on all pages */}
        <Header 
          onLogout={logout}
          showNavigation={true}
        />
        
        {/* Main content */}
        <main>
          <Routes>
            <Route path="/" element={<SearchPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/about" element={<About user={user} />} />
            <Route path="/event/:id" element={<EventDetails />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/tickets" element={<Tickets />} />
            <Route path="/favorites" element={<Favorites />} />
            <Route path="/admin-login" element={<AdminLoginForm />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/master" element={<MasterDashboard />} />
            <Route path="/admin-dashboard" element={<AdminDashboard />} />
            <Route path="/master-dashboard" element={<MasterDashboard />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>

        {/* Bottom Navigation Bar (Mobile) */}
        <BottomNavBar />

        {/* WhatsApp Chat Widget */}
        <WhatsAppChat />

        {/* Auth Modal for Login/Signup */}
        <AuthModal />

        {/* Toast Notifications */}
        <ToastContainer toasts={toasts} onClose={removeToast} />
      </div>
    </Router>
  );
};

/**
 * Main App component with routing and internationalization
 */
const AppContent: React.FC = () => {
  return <AppContentInner />;
};

function App() {
  return (
    <SitePasswordGate>
      <ThemeProvider>
        <AuthProvider>
          <AuthModalProvider>
            <ToastProvider>
              <ErrorBoundary>
                <AppContent />
              </ErrorBoundary>
            </ToastProvider>
          </AuthModalProvider>
        </AuthProvider>
      </ThemeProvider>
    </SitePasswordGate>
  );
}

export default App;
